// v50.157 — Per-user match-weight customization.
//
// Ports v48.67's "Settings → AI matching" sliders. Each viewer can
// rebalance how much each signal contributes to the base match score.
// localStorage is the SYNCHRONOUS read path consumed inline by
// computeBaseScore() so no callers need updating.
//
// v50.336 — Defence P10: weights now also persist per ACCOUNT in
// match_preferences (migration-149, owner-only RLS). localStorage stays
// the source for reads; the DB row is the durable cross-device copy:
//   • sign-in hydrate → primeMatchWeights() fetches the 'global' row
//     and, if present, overwrites the local cache (DB wins);
//   • save → fire-and-forget upsert;
//   • reset → fire-and-forget delete (absence = defaults everywhere).
// Flag off / signed out / fetch error ⇒ exact v50.157 per-device
// behaviour. The sync is structural, never blocking a render.
//
// Range: 0.0–2.0 per dimension. Default 1.0 preserves the v48.27
// baseline. 0.0 disables that signal entirely; 2.0 doubles its
// contribution. Final score is still capped at 95 / floored at the
// 70 baseline so a user cannot push a thin partner into Strong tier.

import { useEffect, useState } from "react";
import { supabase } from "@/lib/api/supabase-client";
import { FLAGS } from "@/lib/flags";

const STORAGE_KEY = "lc-match-weights-v1";
const SCOPE_GLOBAL = "global";

export interface MatchWeights {
  /* reliability_score ≥ 80/90 bonuses */
  reliability: number;
  /* performance_score ≥ 80/90 bonuses */
  performance: number;
  /* partner_progression "trusted" / "framework" bonuses */
  progression: number;
  /* operative_count ≥ 5 bonus */
  crewDepth:   number;
  /* hyperscale T3/T4 + DC specialist bonuses */
  capability:  number;
}

export const DEFAULT_MATCH_WEIGHTS: MatchWeights = {
  reliability: 1.0,
  performance: 1.0,
  progression: 1.0,
  crewDepth:   1.0,
  capability:  1.0,
};

export function clampWeight(v: number): number {
  if (!Number.isFinite(v)) return 1.0;
  return Math.max(0, Math.min(2, Math.round(v * 10) / 10));
}

export function getMatchWeights(): MatchWeights {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_MATCH_WEIGHTS;
    const parsed = JSON.parse(raw);
    return {
      reliability: clampWeight(Number(parsed.reliability ?? 1)),
      performance: clampWeight(Number(parsed.performance ?? 1)),
      progression: clampWeight(Number(parsed.progression ?? 1)),
      crewDepth:   clampWeight(Number(parsed.crewDepth   ?? 1)),
      capability:  clampWeight(Number(parsed.capability  ?? 1)),
    };
  } catch {
    return DEFAULT_MATCH_WEIGHTS;
  }
}

export function setMatchWeights(w: MatchWeights): void {
  const clamped = {
    reliability: clampWeight(w.reliability),
    performance: clampWeight(w.performance),
    progression: clampWeight(w.progression),
    crewDepth:   clampWeight(w.crewDepth),
    capability:  clampWeight(w.capability),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(clamped));
    window.dispatchEvent(new CustomEvent("lc-match-weights-changed"));
  } catch { /* quota — ignore */ }
  void persistToAccount(clamped);
}

export function resetMatchWeights(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("lc-match-weights-changed"));
  } catch { /* ignore */ }
  void deleteAccountRow();
}

/* ── v50.336 — account persistence (Defence P10) ──────────────────── */

/* Cache-only write used by the hydrate path: must NOT round-trip back
   to the DB or a sign-in would re-upsert the row it just read. */
function seedLocalCache(w: MatchWeights): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(w));
    window.dispatchEvent(new CustomEvent("lc-match-weights-changed"));
  } catch { /* quota — ignore */ }
}

async function persistToAccount(w: MatchWeights): Promise<void> {
  if (!FLAGS.persistedMatchWeights) return;
  try {
    const { data } = await supabase.auth.getSession();
    const userId = data?.session?.user?.id;
    if (!userId) return;
    await supabase.from("match_preferences").upsert(
      { user_id: userId, scope: SCOPE_GLOBAL, weights: w, updated_at: new Date().toISOString() },
      { onConflict: "user_id,scope" },
    );
  } catch { /* offline / table missing — device-local copy still works */ }
}

async function deleteAccountRow(): Promise<void> {
  if (!FLAGS.persistedMatchWeights) return;
  try {
    const { data } = await supabase.auth.getSession();
    const userId = data?.session?.user?.id;
    if (!userId) return;
    await supabase.from("match_preferences").delete()
      .eq("user_id", userId).eq("scope", SCOPE_GLOBAL);
  } catch { /* ignore */ }
}

let primedUserId: string | null = null;

/* Fire-and-forget, called from the AuthContext hydrate alongside
   primeMatchModel. If the account has a saved row, it overwrites the
   device cache (DB wins on sign-in); if not, any device-local weights
   survive untouched and sync up on the next explicit save. */
export function primeMatchWeights(userId: string | null | undefined): void {
  if (!FLAGS.persistedMatchWeights || !userId || userId === primedUserId) return;
  primedUserId = userId;
  void (async () => {
    try {
      const { data, error } = await supabase
        .from("match_preferences")
        .select("weights")
        .eq("scope", SCOPE_GLOBAL)
        .maybeSingle();
      if (error || !data?.weights || typeof data.weights !== "object") return;
      const w = data.weights as Record<string, unknown>;
      seedLocalCache({
        reliability: clampWeight(Number(w.reliability ?? 1)),
        performance: clampWeight(Number(w.performance ?? 1)),
        progression: clampWeight(Number(w.progression ?? 1)),
        crewDepth:   clampWeight(Number(w.crewDepth   ?? 1)),
        capability:  clampWeight(Number(w.capability  ?? 1)),
      });
    } catch { /* per-device behaviour stands */ }
  })();
}

/* React hook — re-reads on lc-match-weights-changed so the slider
   preview and live partner profiles re-render together. */
export function useMatchWeights(): MatchWeights {
  const [weights, setWeights] = useState<MatchWeights>(() => getMatchWeights());
  useEffect(() => {
    const refresh = () => setWeights(getMatchWeights());
    window.addEventListener("lc-match-weights-changed", refresh);
    /* Storage event fires on the OTHER tabs when another tab writes —
       handy for the Settings card preview if a user opens it twice. */
    window.addEventListener("storage", (e) => {
      if (e.key === STORAGE_KEY) refresh();
    });
    return () => {
      window.removeEventListener("lc-match-weights-changed", refresh);
    };
  }, []);
  return weights;
}
